import { ToastService } from './toast.service';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Location } from '@angular/common';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent
} from '@angular/router';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Preloader, PreloaderPosition } from 'core/models';

const PENDING_JOBS = 'PENDING_JOBS'; // key to clear on localstorage pending jobs
const PRELOADER_TIMEOUT = 20000; // timeout to force preloader hiding when some task become idle

@Injectable()
export class UiService {
  public navBarSmall = false;
  public navigationCompleted = true;
  public microfrontend: Boolean = true;

  public toolbarConfig = {
    addRoute: false,
    detailRoute: false,
    deleteRoute: false,
    viewModule: 'list',
    sortableFields: false
  };

  /*
  preloader components configuration. Property's values must mimic the corresponding values of the materialize-css library.
    - enabled: true/false, flag to set enabled or not preloader, default true
    - pendingTasks: integer to count number of tasks running, preloader will remain visible while value is greater than 0
    - position: Enum for the preloader position inside App
  */
  public preloaderConfig: Preloader = {
    enabled: true,
    pendingTasks: 0,
    position: PreloaderPosition.Primary
  };

  private preloaderTimeout: any;

  public preloaderConfigSubject = new BehaviorSubject<Preloader>(this.preloaderConfig);

  constructor(
    public toastService: ToastService,
    public router: Router,
    public location: Location,
    public domSanitizer: DomSanitizer
  ) {
    router.events
      .pipe(filter((event: Event | RouterEvent): event is RouterEvent => event instanceof RouterEvent))
      .subscribe((event: RouterEvent): void => this.onChangeRoute(event));
  }

  toggleNavBarSmall(): void {
    this.navBarSmall = !this.navBarSmall;
    this.navigationCompleted = !this.navBarSmall;
  }

  onChangeRoute(routerEvent: RouterEvent): void {
    if (routerEvent instanceof NavigationStart) {
      this.displayPreloader();
      this.toastService.clearNotice();
      this.navigationCompleted = false;
    } else if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.navigationCompleted = true;
      // eslint-disable-next-line
      window.scrollTo(0, 0);
      this.hidePreloader();
    }
  }

  setToolbarConfig(options: any): void {
    Object.assign(this.toolbarConfig, options);
  }

  setPreloaderEnabled(flag: boolean): void {
    this.preloaderConfig.enabled = flag;
  }

  public getPreloaderConfig$(): Observable<Preloader> {
    return this.preloaderConfigSubject.asObservable();
  }

  public setPreloaderConfig(preloader: Preloader): void {
    this.preloaderConfig = preloader;
    this.preloaderConfigSubject.next(preloader);
  }

  // Displays a preloader component
  displayPreloader(): void {
    if (!this.preloaderConfig.enabled) {
      return;
    }
    clearTimeout(this.preloaderTimeout);
    // eslint-disable-next-line
    this.preloaderTimeout = setTimeout(() => this.hidePreloader(true), PRELOADER_TIMEOUT);
    this.preloaderConfig.pendingTasks = this.preloaderConfig.pendingTasks + 1;
    this.preloaderConfigSubject.next(this.preloaderConfig);
  }

  // Hide the preloader component
  hidePreloader(force: boolean = false): void {
    this.preloaderConfig.pendingTasks = Math.max(this.preloaderConfig.pendingTasks - 1, 0);
    if (force || this.preloaderConfig.pendingTasks <= 0) {
      clearTimeout(this.preloaderTimeout);
      this.preloaderConfig.pendingTasks = 0;
      this.preloaderConfigSubject.next(this.preloaderConfig);
    }
  }

  hideMicrofrontend(): void {
    this.microfrontend = false;
  }

  setLoginEnvironment(credentials: string = '', forcePath?: string): void {
    //Clear jobs when logout
    localStorage.removeItem(PENDING_JOBS);
    this.grabCurrentLocation(forcePath);
    // eslint-disable-next-line
    window.location.replace(
      `${environment.servicesPlatform}/login?sid=${environment.SERVICE_ID}${
        credentials ? `&credentials=${credentials}` : ''
      }`
    );
  }

  grabCurrentLocation(forcePath?: string): void {
    if (forcePath) {
      return this.grabLocation(forcePath);
    }
    const entryPaths = ['/login', '/entry', '/activate', '/change-password', '/forgot-password'];
    const path = this.location.path(true);
    if (!entryPaths.includes(path)) {
      this.grabLocation(this.location.path(true));
    }
  }

  grabLocation(location: any): void {
    if (location) {
      const [path, query] = location.split('?');
      let queryString = '';
      if (query) {
        const searchParams: any = new URLSearchParams(query);
        queryString = `?${searchParams.toString()}`;
      }

      sessionStorage.setItem('landing', `${path}${queryString}`);
    }
  }

  getLandingUrl(): string {
    return sessionStorage.getItem('landing');
  }

  setLandingUrl(): void {
    const landing = this.getLandingUrl() || '';
    sessionStorage.removeItem('landing');
    this.router.navigateByUrl(landing, { replaceUrl: true }).then();
  }
}
